import { useAtomValue } from "jotai";
import { useMemo } from "react";
import * as yup from "yup";

import { accountAtom } from "shared/lib/account";

import type {
  Education,
  School,
  StudentStaticFields,
} from "scmp-app/components/posties/student-form/types";

import { RequiredMessage } from "./consts";

const phoneRegExp = /^(?:\+?\d{1,3})?\s?\(?\d{1,4}?\)?[\s.-]?\d{1,4}[\s.-]?\d{1,9}$/;
const emailRegExp = /^[\w%+.-]+@[\d.A-Za-z-]+\.[A-Za-z]{2,}$/;

export const useStudentFormSchema = () => {
  const { user } = useAtomValue(accountAtom);
  const studentFormFields: StudentStaticFields = useMemo(
    () => ({
      address: {
        inputProps: { type: "text" },
        type: "text",
      },
      artwork: {
        type: "file",
      },
      dateOfBirth: {
        defaultValue: null,
        type: "date",
      },
      email: {
        defaultValue: user?.useremail ?? "",
        inputProps: { type: "email" },
        type: "text",
        validation: yup
          .string()
          .transform(value => ((value as string) === "" ? null : (value as string)))
          .matches(emailRegExp, "Email must be a valid email")
          .nullable(),
      },
      facebook: {
        inputProps: { type: "text" },
        type: "text",
      },
      instagram: {
        inputProps: { type: "text" },
        type: "text",
      },
      name: {
        defaultValue: user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : "",
        inputProps: { type: "text" },
        type: "text",
      },
      phone: {
        inputProps: { type: "text" },
        type: "text",
        validation: yup
          .string()
          .transform(value => ((value as string) === "" ? null : (value as string)))
          .matches(phoneRegExp, "Phone number is not valid")
          .nullable(),
      },
      photo: {
        type: "file",
      },
      school: {
        autoCompleteProps: {
          getOptionLabel: option => {
            const customOptionLabel = (option as School)?.name;
            return typeof option === "string" ? option : customOptionLabel;
          },
          renderOption: (props, option: School) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const { key, ...optionProps } = props;
            return (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              <li key={key} {...optionProps}>
                {option.name}
              </li>
            );
          },
        },
        defaultValue: null,
        others: {
          field: {
            display: true,
            inputProps: { type: "text" },
            order: null,
            placeholder: null,
            required: false,
            title: "Other School",
            type: "text",
            validation: yup.string().when("school", {
              is: (school: School) => school?.documentId === "other",
              otherwise: schema => schema.nullable(),
              // eslint-disable-next-line unicorn/no-thenable
              then: schema => schema.required(RequiredMessage),
            }),
            validationType: null,
          },
          fieldKey: "otherSchool",
          showOptionDocumentId: "other",
        },
        type: "school",
        validation: yup
          .object({
            documentId: yup.string().required(RequiredMessage),
            name: yup.string().required(),
          })
          .nullable(),
        withOther: true,
      },
      yearOrGrade: {
        autoCompleteProps: {
          getOptionLabel: option => {
            const customOptionLabel = `${(option as Education).name}`;
            return typeof option === "string" ? option : customOptionLabel;
          },
          renderOption: (props, option: Education) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const { key, ...optionProps } = props;
            return (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              <li key={key} {...optionProps}>
                {option.name}
              </li>
            );
          },
        },
        defaultValue: null,
        others: {
          field: {
            display: true,
            inputProps: { type: "text" },
            order: null,
            placeholder: null,
            required: false,
            title: "Other Year/Grade",
            type: "text",
            validation: yup.string().when("yearOrGrade", {
              is: (yearOrGrade: School) => yearOrGrade?.documentId === "fp3eq02ch20wj08f11l2w4yu",
              otherwise: schema => schema.nullable(),
              // eslint-disable-next-line unicorn/no-thenable
              then: schema => schema.required(RequiredMessage),
            }),
            validationType: null,
          },
          fieldKey: "otherYearOrGrade",
          showOptionDocumentId: "fp3eq02ch20wj08f11l2w4yu",
        },
        type: "autoComplete",
        validation: yup
          .object({
            documentId: yup.string().required(RequiredMessage),
            isOther: yup.boolean().nullable(),
            name: yup.string().required(),
            schoolType: yup.string().required(),
          })
          .nullable("Please select a year or grade"),
      },
    }),
    [user],
  );

  return studentFormFields;
};
